import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { PagedSearchResults } from '@bayclubs/utils';
import { BayclubsAlertService } from '@bayclubs/alert';

import { camps } from '../../../models/models.generated';
import { ReportsApiService } from '../../../services/api/reports-api.service';
import { ReportsEventService, ReportType } from '../reports-event.service';
import { CapacityReportFilter } from '../../../models/filters/reports-filters.model';
import { FiltersHelperService } from '../../../services/filters-helper.service';

@Component({
    selector: 'app-capacity-report',
    templateUrl: './capacity-report.component.html',
    styleUrls: ['./capacity-report.component.scss'],
    standalone: false
})
export class CapacityReportComponent implements OnInit {
    public searchForm: UntypedFormGroup;

    public items: camps.CapacityReportInfoItem[] = [];
    public isBusy: boolean = false;

    public searchResults: PagedSearchResults = new PagedSearchResults();

    public pageSize: number = 100;

    private onSearchSubscription;

    public filters: CapacityReportFilter;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private reportsApi: ReportsApiService,
        private alert: BayclubsAlertService,
        private reportsEvent: ReportsEventService,
        private filtersHelperService: FiltersHelperService
    ) {
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.initForm(this.filters);
    }

    ngOnDestroy() {
        if (this.onSearchSubscription) {
            this.onSearchSubscription.unsubscribe();
            this.onSearchSubscription = null;
        }
    }

    private initForm(filter: CapacityReportFilter): void {
        this.searchForm = this.formBuilder.group({
            dateFrom: [filter.dateFrom, []],
            dateTo: [filter.dateTo, []],
            sessionName: [filter.sessionName, []],
            clubIds: [filter.clubIds, []],
            categories: [filter.categories, []],
            campTypes: [filter.campTypes, []]
        });

        this.onSearchSubscription = this.reportsEvent.search.subscribe(() => {
            this.onSearch();
        });
    }

    public onSearch(numberOfPagesToSkip: number = 0) {
        let request = this.buildRequest(numberOfPagesToSkip, this.pageSize);

        this.isBusy = true;

        if (numberOfPagesToSkip === 0) {
            this.items = [];
        }

        this.reportsApi.searchForCapacityReport(request).subscribe(
            x => {
                this.searchResults.pageNumber = x.pageNumber;
                this.searchResults.totalCount = x.totalCount;
                this.searchResults.totalPages = x.totalPages;
                this.searchResults.pages = Array<number>();

                const filter: CapacityReportFilter = <CapacityReportFilter>{
                    dateFrom: this.searchForm.controls['dateFrom'].value,
                    dateTo: this.searchForm.controls['dateTo'].value,
                    sessionName: this.searchForm.controls['sessionName'].value,
                    clubIds: this.searchForm.controls['clubIds'].value,
                    categories: this.searchForm.controls['categories'].value,
                    campTypes: this.searchForm.controls['campTypes'].value,
                    reportType: ReportType.Capacity
                };

                this.filtersHelperService.mergeQueryParam(filter, 'CapacityReport', 'capacityFilter');

                for (var j = 1; j <= this.searchResults.totalPages; j++) {
                    this.searchResults.pages.push(j);
                }

                for (var i = 0; i < x.pageItems.length; i++) {
                    this.items.push(x.pageItems[i]);
                }
            },
            error => {
                this.alert.showError(error);
            },
            () => {
                this.isBusy = false;
            }
        );
    }

    public get noItems() {
        return this.items == null || this.items.length == 0;
    }

    public getReport() {
        let request = this.buildRequest();

        this.reportsApi.getCapacityReport(request);
    }

    public onScroll() {
        if (this.searchResults.canNextPage) {
            this.onSearch(this.searchResults.nextPageNumber);
        }
    }

    private buildRequest(numberOfPagesToSkip: number = 0, take: number = null): camps.CapacityReportRequest {
        return <camps.CapacityReportRequest>{
            dateFrom: this.searchForm.controls['dateFrom'].value,
            dateTo: this.searchForm.controls['dateTo'].value,
            categories: this.searchForm.controls['categories'].value,
            clubs: this.searchForm.controls['clubIds'].value,
            sessionName: this.searchForm.controls['sessionName'].value,
            campTypes: this.searchForm.controls['campTypes'].value,
            take: take,
            skip: numberOfPagesToSkip * take
        };
    }

    private getFilter() {
        const defaultFilter: CapacityReportFilter = {
            dateFrom: null,
            dateTo: null,
            sessionName: null,
            clubIds: [],
            categories: [],
            campTypes: [],
            reportType: ReportType.Capacity
        };

        const restoredFilter = this.filtersHelperService.restoreFilter<CapacityReportFilter>(
            'CapacityReport',
            'capacityFilter'
        );

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
